import { createEffect, createSignal, DEV, on, onCleanup, type Accessor } from "solid-js"
import { type Mesh, type Scene } from "three"
import { createSharedTileGraphNodeCollectionMemo } from "../../../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import useGridWidthHeight from "../../../../../rx/memo/useGridWidthHeight"
import { useNodeNeighborBaseFloorElevationReactiveMap } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import { useSharedGrassBatchCountListMemo } from "../../../../../rx/memo/useSharedGrassBatchCountListMemo"
import { useSharedManhattanDistanceToLandListMemo } from "../../../../../rx/memo/useSharedManhattanDistanceToLandListMemo"
import isIdListSame from "../../../../../rx/signal/equals/isIdListSame"
import type { Engine } from "../../../../core/engine/Engine.type"
import type { TileGraphNode } from "../../../../core/engine/TileGraphNode/TileGraphNode.type"
import type { EntityId } from "../../../../core/entity/EntityId.type"
import { isEntityRiver, isEntityShoal } from "../../../../core/entity/entityTypeMetaList.generated"
import type { Entity } from "../../../../core/entity/index"
import Unexpected from "../../../../Exception/Unexpected.class"
import createEntityQuadtreeEffect, { type QuadrantBound } from "../../../../Quadtree/createEntityQuadtreeEffect"
import dispatchPromiseBasedJob from "../../../../WebWorker/dispatchPromiseBasedJob"
import getNextWebWorker from "../../../../WebWorker/getNextWebWorker"
import disposeRecursive from "../../../fn/disposeRecursive"
import { unpackPosUVColorSwayIndGeometry } from "../../../fn/PackGeometry/PosUVColorSwayInd"
import { useRootQuadrantBound } from "../../../sky/useRootQuadrantBound"
import createGrassWavesObject from "./createGrassWavesObject"
import createTileGraphNodeGrassWavesGeometryV1WorkerInput from "./createTileGraphNodeGrassWavesGeometryV1WorkerInput"
import grassWaveEntitySecondChance from "./grassWaveEntitySecondChance"
import isEntityNeverGrassWaves from "./isEntityNeverGrassWaves"
import isEntityWithGrassWaves from "./isEntityWithGrassWaves"
import createGrassWavesGeometryJobData from "./Job/createGrassWavesGeometryJobData"
import type { CreateTileGraphNodeGrassWavesGeometryV1DataResult } from "./Job/CreateTileGraphNodeGrassWavesGeometryV1DataResult.type"
import rngSaysEntHasGrassWaves from "./rngSaysEntHasGrassWaves"

export default function createGrassWavesEffect(
  engine: Engine,
  scene: Scene,
) {
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)
  const getDistanceToLandList = useSharedManhattanDistanceToLandListMemo(engine)
  const getGrassBatchCountList = useSharedGrassBatchCountListMemo(engine)
  const getGridSize = useGridWidthHeight(engine)
  const getRootQuadrantBound = useRootQuadrantBound(engine)

  createEffect(() => {
    // if (DEV) {
    //   // temporary disable because it makes map editor PaintTile slow
    //   return
    // }
    const gridWidth = getGridSize().width
    const tpNodeList = getTileGraphNodesList()
    // if empty, don't bother
    if (neighborBaseElevationReactiveMap.size > 0) {

      const [getGrassEntList, setGrassEntList] = createSignal<Array<Entity>>([], {
        equals: isIdListSame,
      })

      function getEntNode(ent: Entity): TileGraphNode {
        const nodeIndex = ent.x + ent.y * gridWidth
        const node = tpNodeList[nodeIndex]
        if (DEV) {
          if (!node) {
            throw new Unexpected(`!tpNodeList[${nodeIndex}]`)
          }
        }
        return node!
      }

      createEffect(() => {
        const indexSet: Set<number> = new Set()
        const grassEntIdList: Array<EntityId> = []
        const grassEntList: Array<Entity> = []
        const shoalOrRiverIndexSet: Set<number> = new Set()

        const distanceToLandList = getDistanceToLandList()
        // console.log('distanceToLandList', distanceToLandList)

        for (let i = 0; i < tpNodeList.length; i++) {
          const tpNode = tpNodeList[i]
          const entsList = tpNode.ents()
          const entWithGrass = entsList.find(isEntityWithGrassWaves)
          if (entWithGrass && !entsList.find(isEntityNeverGrassWaves)) {
            const distanceToLand = distanceToLandList[i] || 0
            if (rngSaysEntHasGrassWaves(entWithGrass, distanceToLand)) {
              indexSet.add(i)
              grassEntIdList.push(entWithGrass.id)
              grassEntList.push(entWithGrass)
            }
            if (isEntityShoal(entWithGrass) || isEntityRiver(entWithGrass)) {
              shoalOrRiverIndexSet.add(i)
            }
          }
        }

        // shoals give seas a second chance to be grassed
        for (const index of shoalOrRiverIndexSet) {
          // indexSet.delete(index)
          const shoalOrRiverNode: TileGraphNode = tpNodeList[index]!
          const shoalOrRiverEntId = shoalOrRiverNode.ents().find(isEntityWithGrassWaves)!.id
          grassWaveEntitySecondChance(shoalOrRiverNode.n, indexSet, shoalOrRiverEntId, grassEntIdList, grassEntList, distanceToLandList)
          grassWaveEntitySecondChance(shoalOrRiverNode.w, indexSet, shoalOrRiverEntId, grassEntIdList, grassEntList, distanceToLandList)
          grassWaveEntitySecondChance(shoalOrRiverNode.s, indexSet, shoalOrRiverEntId, grassEntIdList, grassEntList, distanceToLandList)
          grassWaveEntitySecondChance(shoalOrRiverNode.e, indexSet, shoalOrRiverEntId, grassEntIdList, grassEntList, distanceToLandList)
        }

        // console.log('createGrassWavesEffect.effect.setGrassEntList')
        setGrassEntList(grassEntList)
      })

      // createEffect(() => {
      //   console.log('createGrassWavesEffect.getGrassEntList().length', getGrassEntList().length)
      // })

      const prevMeshMap = new Map<QuadrantBound, Mesh>()
      function cleanupPrevMeshMap() {
        prevMeshMap.forEach(disposeRecursive)
        prevMeshMap.clear()
      }
      createEffect(on(getRootQuadrantBound, cleanupPrevMeshMap))
      onCleanup(cleanupPrevMeshMap)
      function replacePrevMesh(quadrant: QuadrantBound, mesh: Mesh | null) {
        const prevMesh1 = prevMeshMap.get(quadrant)
        if (prevMesh1) {
          disposeRecursive(prevMesh1)
        }
        if (mesh) {
          prevMeshMap.set(quadrant, mesh)
          scene.add(mesh)
        } else {
          prevMeshMap.delete(quadrant)
        }
      }
      const maxChunkSize = 64
      const maxNesting = 3
      createEntityQuadtreeEffect(getGrassEntList, getRootQuadrantBound, maxChunkSize, maxNesting, (
        chunkedEnts: Accessor<Array<Entity>>,
        quadrant: QuadrantBound,
      ): void => {
        // console.log('createGrassWavesEffect.chunk', quadrant, '.getGrassEntList().length', chunkedEnts().length)
        const ents = chunkedEnts()
        const nodeList1 = ents.map(getEntNode)
        if (nodeList1.length > 0) {
          // console.time(`createGrassWavesEffect.chunk(x:${quadrant.x},y:${quadrant.y}:len:${nodeList1.length})`)
          const grassBatchCountList = getGrassBatchCountList()
          const geometryDataInput = createTileGraphNodeGrassWavesGeometryV1WorkerInput(
            nodeList1, neighborBaseElevationReactiveMap)

          // const geometryData = createTileGraphNodeGrassWavesGeometryV1Data(
          //   geometryDataInput, grassBatchCountList)

          // console.time(`createGrassWavesEffect.chunk(x:${quadrant.x},y:${quadrant.y}:len:${nodeList1.length}).job_from_outside`)
          const resultPromise
            = dispatchPromiseBasedJob<CreateTileGraphNodeGrassWavesGeometryV1DataResult>(
              getNextWebWorker(),
              createGrassWavesGeometryJobData(geometryDataInput,
                grassBatchCountList),
            )

          resultPromise.then((
            result: CreateTileGraphNodeGrassWavesGeometryV1DataResult
          ) => {
            // console.timeEnd(`createGrassWavesEffect.chunk(x:${quadrant.x},y:${quadrant.y}:len:${nodeList1.length}).job_from_outside`)
            const geometry = unpackPosUVColorSwayIndGeometry(result)
            const mesh = createGrassWavesObject(engine, geometry)
            replacePrevMesh(quadrant, mesh)
            // console.timeEnd(`createGrassWavesEffect.chunk(x:${quadrant.x},y:${quadrant.y}:len:${nodeList1.length})`)
          })
        } else {
          replacePrevMesh(quadrant, null)
        }
      })
    }
  })
}
